import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Clock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import CurrencyIcon from '../components/CurrencyIcon';
import api from '../utils/api';
import Header from '../components/Header';

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const exchangeData = location.state?.exchangeData;

  const [timeLeft, setTimeLeft] = useState(17 * 60);
  const [copied, setCopied] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestId = location.state?.requestId;
  const requestNumber = location.state?.requestNumber || Math.floor(1000000 + Math.random() * 9000000);
  
  const COMPANY_WALLETS = {
    BTC: { address: 'bc1qxwyvtdvkufulqt0lpjw8rylxtxgh03ff8372zq', network: 'Bitcoin' },
    USDT: { address: 'TRktGijGuyyufnxCkuz45a6byFXFyfX2DK', network: 'Tron (TRC20)' },
    USDT_ERC20: { address: '0x41E61f6531d6562Dd1E90102Bc8E85BF3e920E0B', network: 'Ethereum (ERC20)' },
  };

  const getWalletAddress = () => {
    const crypto = exchangeData?.give_method?.symbol || 'BTC';
    if (COMPANY_WALLETS[crypto]) return COMPANY_WALLETS[crypto].address;
    return null;
  };

  const fullWalletAddress = getWalletAddress();

  useEffect(() => {
    if (!exchangeData) {
      navigate('/exchange');
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error('Le délai de paiement a expiré');
          navigate('/exchange');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [exchangeData, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Adresse copiée !');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    try {
      if (requestId) {
        await api.post(`/exchange/confirm-payment/${requestId}`);
      }
      setPaymentConfirmed(true);
      toast.success('Paiement confirmé ! Votre demande est en cours de traitement.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = () => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler cette demande ?')) {
      toast.success('Demande annulée');
      navigate('/exchange');
    }
  };

  if (!exchangeData) {
    return null;
  }

  const isSelling = exchangeData.mode === 'sell';
  const cryptoSymbol = isSelling ? exchangeData.give_method.symbol : exchangeData.receive_method.symbol;
  const cryptoAmount = isSelling ? exchangeData.give_amount : exchangeData.receive_amount;
  const fiatAmount = isSelling ? exchangeData.receive_amount : exchangeData.give_amount;
  const fiatMethod = isSelling ? exchangeData.receive_method : exchangeData.give_method;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <Header />

      <div className="pt-10 px-6 pb-12" data-testid="payment-container">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center" style={{  }} data-testid="page-title">
            Échange {cryptoSymbol} vers {fiatMethod.name}
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Order Summary */}
            <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)] " data-testid="order-summary">
              <div className="flex items-center justify-between mb-6">
                <span className="text-gray-400">Je donne</span>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-gray-900">{exchangeData.give_amount}</span>
                  <div className="flex items-center space-x-1 bg-gray-50 px-3 py-1 rounded-lg">
                    {exchangeData.give_method.symbol ? <CurrencyIcon symbol={exchangeData.give_method.symbol} size={22} /> : null}
                    <span className="text-gray-600">{exchangeData.give_method.symbol || exchangeData.give_method.name}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mb-6">
                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                  <ArrowLeft className="w-4 h-4 rotate-[-90deg] text-gray-400" />
                </div>
              </div>

              <div className="flex items-center justify-between mb-6">
                <span className="text-gray-400">Je reçois</span>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-gray-900">{exchangeData.receive_amount}</span>
                  <div className="flex items-center space-x-1 bg-gray-50 px-3 py-1 rounded-lg">
                    {exchangeData.receive_method.symbol ? <CurrencyIcon symbol={exchangeData.receive_method.symbol} size={22} /> : null}
                    <span className="text-gray-600">{exchangeData.receive_method.symbol || exchangeData.receive_method.name}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Numéro de téléphone / Compte</span>
                  <span className="text-gray-900 font-semibold">{exchangeData.receive_account_info || '-'}</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Taux</span>
                  <span className="text-gray-900 font-semibold">1 {cryptoSymbol} = {(fiatAmount / cryptoAmount).toFixed(2)} {fiatMethod.name === 'Virement SEPA' ? 'EUR' : 'EUR'}</span>
                </div>
              </div>

              <button
                onClick={handleCancelRequest}
                className="w-full bg-gray-50 text-gray-600 py-3 rounded-xl hover:bg-gray-200 transition-colors font-medium border border-gray-100"
                data-testid="cancel-btn"
              >
                Annuler la demande
              </button>
            </div>

            {/* Right Column - Payment Instructions */}
            <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)] " data-testid="payment-instructions">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-gray-400 text-sm">Demande N°</span>
                  <span className="ml-2 font-mono font-bold text-gray-900">{requestNumber}</span>
                </div>
                <div className="text-sm text-gray-400">
                  {new Date().toLocaleString('fr-FR')}
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between mb-3 bg-[#191C1F]/5 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center space-x-2 text-[#191C1F]">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="font-medium">En attente du paiement</span>
                </div>
                <div className="flex items-center space-x-2 bg-[#191C1F]/10 px-3 py-1 rounded-lg">
                  <Clock className="w-4 h-4 text-[#191C1F]" />
                  <span className="font-mono font-bold text-[#191C1F]">{formatTime(timeLeft)}</span>
                </div>
              </div>

              {exchangeData.rate_lock_expires_at && (
                <div className="flex items-center justify-between mb-6 bg-emerald-50 rounded-xl p-4 border border-emerald-200" data-testid="rate-lock-confirmation">
                  <span className="font-medium text-emerald-700 text-sm">Taux verrouillé pour cette transaction</span>
                  <span className="font-mono font-bold text-emerald-700 text-sm">
                    {new Date(exchangeData.rate_lock_expires_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}

              {/* Amount */}
              <div className="mb-6">
                <span className="text-gray-400 text-sm">À payer</span>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-3xl font-bold text-[#191C1F]">{cryptoAmount}</span>
                  <div className="flex items-center space-x-1">
                    {exchangeData.give_method.symbol ? <CurrencyIcon symbol={exchangeData.give_method.symbol} size={22} /> : null}
                    <span className="text-xl font-bold text-gray-900">{cryptoSymbol}</span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(cryptoAmount.toString())}
                    className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              {/* Step 1 */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100" data-testid="step-1">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-[#191C1F] flex items-center justify-center text-white font-bold text-sm">1</div>
                  <span className="font-medium text-gray-900">Envoyez la cryptomonnaie à cette adresse</span>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-1">
                    <div className="text-sm text-gray-400 mb-1">Devise</div>
                    <div className="font-bold text-gray-900 mb-3">{cryptoSymbol}</div>
                    <div className="text-sm text-gray-400 mb-1">Adresse</div>
                    <div className="bg-white rounded-lg p-3 flex items-center justify-between border border-gray-100">
                      <span className="font-mono text-sm text-gray-600 truncate pr-2">{fullWalletAddress || "Adresse communiquée par email après validation de la demande"}</span>
                      <button 
                        onClick={() => copyToClipboard(fullWalletAddress)}
                        className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100" data-testid="step-2">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-[#191C1F] flex items-center justify-center text-white font-bold text-sm">2</div>
                  <span className="font-medium text-gray-900">Confirmez votre transfert</span>
                </div>

                <button
                  onClick={handleConfirmPayment}
                  disabled={loading || paymentConfirmed}
                  className="w-full bg-[#191C1F] text-white py-4 rounded-xl hover:bg-[#191C1F]/80 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-payment-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center space-x-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Confirmation en cours...</span>
                    </span>
                  ) : paymentConfirmed ? (
                    <span className="flex items-center justify-center space-x-2">
                      <Check className="w-5 h-5" />
                      <span>Paiement confirmé</span>
                    </span>
                  ) : (
                    "J'ai payé"
                  )}
                </button>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between text-sm pt-4 border-t border-gray-100">
                <a href="/agreement" className="text-gray-900 font-semibold hover:underline">
                  Conditions d'échange →
                </a>
                <div className="flex items-center space-x-2 text-gray-400">
                  <span>Recommandés par</span>
                  <span className="bg-gray-900 text-white px-2 py-1 rounded font-bold text-xs">BEST CHANGE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
